import React, { useState, useEffect } from 'react';

interface AnalyticsData {
  totalScans: number;
  authenticScans: number;
  duplicateScans: number;
  unverifiedScans: number;
  scansByLocation: Array<{
    location: string;
    count: number;
    authentic: number;
    duplicates: number;
  }>;
  scansByProduct: Array<{
    productName: string;
    totalScans: number;
    authenticScans: number;
    duplicateScans: number;
  }>;
  recentScans: Array<{
    id: string;
    productName: string;
    batchCode: string;
    result: 'authentic' | 'duplicate' | 'unverified';
    location: string;
    timestamp: string;
  }>;
  scanTrends: Array<{
    date: string;
    scans: number;
    authentic: number;
    duplicates: number;
  }>;
}

const ScanAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalScans: 0,
    authenticScans: 0,
    duplicateScans: 0,
    unverifiedScans: 0,
    scansByLocation: [],
    scansByProduct: [],
    recentScans: [],
    scanTrends: []
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      // TODO: Replace with actual API call
      // Simulated data for now
      setTimeout(() => {
        setAnalytics({
          totalScans: 1247,
          authenticScans: 1089,
          duplicateScans: 142,
          unverifiedScans: 16,
          scansByLocation: [
            { location: 'Nairobi', count: 456, authentic: 398, duplicates: 58 },
            { location: 'Mombasa', count: 234, authentic: 201, duplicates: 33 },
            { location: 'Kisumu', count: 189, authentic: 167, duplicates: 22 },
            { location: 'Nakuru', count: 156, authentic: 134, duplicates: 22 },
            { location: 'Eldoret', count: 123, authentic: 108, duplicates: 15 }
          ],
          scansByProduct: [
            { productName: 'Coca-Cola 500ml', totalScans: 567, authenticScans: 489, duplicateScans: 78 },
            { productName: 'Sprite 350ml', totalScans: 345, authenticScans: 301, duplicateScans: 44 },
            { productName: 'Fanta Orange 500ml', totalScans: 234, authenticScans: 198, duplicateScans: 36 }
          ],
          recentScans: [
            {
              id: '1',
              productName: 'Coca-Cola 500ml',
              batchCode: 'CC240315',
              result: 'authentic',
              location: 'Nairobi',
              timestamp: '2 minutes ago'
            },
            {
              id: '2',
              productName: 'Sprite 350ml',
              batchCode: 'SP240314',
              result: 'duplicate',
              location: 'Mombasa',
              timestamp: '15 minutes ago'
            },
            {
              id: '3',
              productName: 'Fanta Orange 500ml',
              batchCode: 'FO240313',
              result: 'authentic',
              location: 'Kisumu',
              timestamp: '1 hour ago'
            },
            {
              id: '4',
              productName: 'Coca-Cola 500ml',
              batchCode: 'CC240312',
              result: 'duplicate',
              location: 'Nakuru',
              timestamp: '2 hours ago'
            }
          ],
          scanTrends: [
            { date: '2024-11-01', scans: 89, authentic: 78, duplicates: 11 },
            { date: '2024-11-02', scans: 134, authentic: 117, duplicates: 17 },
            { date: '2024-11-03', scans: 156, authentic: 134, duplicates: 22 },
            { date: '2024-11-04', scans: 178, authentic: 152, duplicates: 26 },
            { date: '2024-11-05', scans: 201, authentic: 174, duplicates: 27 },
            { date: '2024-11-06', scans: 234, authentic: 203, duplicates: 31 },
            { date: '2024-11-07', scans: 255, authentic: 231, duplicates: 24 }
          ]
        });
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setLoading(false);
    }
  };

  const getResultColor = (result: string) => {
    switch (result) {
      case 'authentic': return '#22c55e';
      case 'duplicate': return '#f59e0b';
      case 'unverified': return '#ef4444';
      default: return '#64748b';
    }
  };

  const getResultIcon = (result: string) => {
    switch (result) {
      case 'authentic': return '✅';
      case 'duplicate': return '⚠️';
      case 'unverified': return '❌';
      default: return '❓';
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        padding: '40px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '20px',
          textAlign: 'center',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#64748b', margin: 0 }}>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      padding: '40px 20px'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: 'white',
          padding: '30px 40px',
          borderRadius: '20px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          marginBottom: '30px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '20px'
          }}>
            <div>
              <h1 style={{
                fontSize: '2rem',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '8px'
              }}>
                Scan Analytics
              </h1>
              <p style={{
                color: '#64748b',
                fontSize: '16px',
                margin: 0
              }}>
                Monitor product verification activity and detect counterfeit patterns
              </p>
            </div>
            
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              style={{
                padding: '8px 16px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: 'white'
              }}
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </select>
          </div>
        </div>

        {/* Overview Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          {/* Total Scans */}
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <h3 style={{
                color: '#374151',
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                margin: 0
              }}>
                Total Scans
              </h3>
              <div style={{
                backgroundColor: '#eff6ff',
                padding: '8px',
                borderRadius: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>📊</span>
              </div>
            </div>
            <p style={{
              fontSize: '2.5rem',
              fontWeight: '700',
              color: '#1e293b',
              margin: '0 0 8px 0'
            }}>
              {analytics.totalScans.toLocaleString()}
            </p>
            <p style={{
              color: '#64748b',
              fontSize: '14px',
              margin: 0
            }}>
              All verification attempts
            </p>
          </div>

          {/* Authentic Scans */}
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <h3 style={{
                color: '#374151',
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                margin: 0
              }}>
                Authentic
              </h3>
              <div style={{
                backgroundColor: '#f0fdf4',
                padding: '8px',
                borderRadius: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>✅</span>
              </div>
            </div>
            <p style={{
              fontSize: '2.5rem',
              fontWeight: '700',
              color: '#22c55e',
              margin: '0 0 8px 0'
            }}>
              {analytics.authenticScans.toLocaleString()}
            </p>
            <p style={{
              color: '#64748b',
              fontSize: '14px',
              margin: 0
            }}>
              {((analytics.authenticScans / analytics.totalScans) * 100).toFixed(1)}% of total scans
            </p>
          </div>

          {/* Duplicate Scans */}
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <h3 style={{
                color: '#374151',
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                margin: 0
              }}>
                Duplicates
              </h3>
              <div style={{
                backgroundColor: '#fef3c7',
                padding: '8px',
                borderRadius: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>⚠️</span>
              </div>
            </div>
            <p style={{
              fontSize: '2.5rem',
              fontWeight: '700',
              color: '#f59e0b',
              margin: '0 0 8px 0'
            }}>
              {analytics.duplicateScans.toLocaleString()}
            </p>
            <p style={{
              color: '#64748b',
              fontSize: '14px',
              margin: 0
            }}>
              {((analytics.duplicateScans / analytics.totalScans) * 100).toFixed(1)}% potential counterfeits
            </p>
          </div>

          {/* Unverified Scans */}
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <h3 style={{
                color: '#374151',
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                margin: 0
              }}>
                Unverified
              </h3>
              <div style={{
                backgroundColor: '#fef2f2',
                padding: '8px',
                borderRadius: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>❌</span>
              </div>
            </div>
            <p style={{
              fontSize: '2.5rem',
              fontWeight: '700',
              color: '#ef4444',
              margin: '0 0 8px 0'
            }}>
              {analytics.unverifiedScans.toLocaleString()}
            </p>
            <p style={{
              color: '#64748b',
              fontSize: '14px',
              margin: 0
            }}>
              {((analytics.unverifiedScans / analytics.totalScans) * 100).toFixed(1)}% unknown products
            </p>
          </div>
        </div>

        {/* Charts and Tables Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '30px',
          marginBottom: '30px'
        }}>
          {/* Scans by Location */}
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{
              color: '#1e293b',
              fontSize: '1.2rem',
              fontWeight: '600',
              marginBottom: '24px'
            }}>
              Scans by Location
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {analytics.scansByLocation.map((location, index) => (
                <div key={index} style={{
                  padding: '16px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <h4 style={{
                      color: '#1e293b',
                      fontSize: '16px',
                      fontWeight: '600',
                      margin: 0
                    }}>
                      {location.location}
                    </h4>
                    <span style={{
                      color: '#64748b',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      {location.count} scans
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    fontSize: '12px'
                  }}>
                    <span style={{ color: '#22c55e' }}>
                      ✅ {location.authentic} authentic
                    </span>
                    <span style={{ color: '#f59e0b' }}>
                      ⚠️ {location.duplicates} duplicates
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div style={{
                    width: '100%',
                    height: '6px',
                    backgroundColor: '#e2e8f0',
                    borderRadius: '3px',
                    marginTop: '8px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${(location.authentic / location.count) * 100}%`,
                      height: '100%',
                      backgroundColor: '#22c55e',
                      borderRadius: '3px'
                    }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Scans by Product */}
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{
              color: '#1e293b',
              fontSize: '1.2rem',
              fontWeight: '600',
              marginBottom: '24px'
            }}>
              Scans by Product
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {analytics.scansByProduct.map((product, index) => (
                <div key={index} style={{
                  padding: '16px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <h4 style={{
                      color: '#1e293b',
                      fontSize: '16px',
                      fontWeight: '600',
                      margin: 0
                    }}>
                      {product.productName}
                    </h4>
                    <span style={{
                      color: '#64748b',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      {product.totalScans} scans
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    fontSize: '12px'
                  }}>
                    <span style={{ color: '#22c55e' }}>
                      ✅ {product.authenticScans} authentic
                    </span>
                    <span style={{ color: '#f59e0b' }}>
                      ⚠️ {product.duplicateScans} duplicates
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div style={{
                    width: '100%',
                    height: '6px',
                    backgroundColor: '#e2e8f0',
                    borderRadius: '3px',
                    marginTop: '8px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${(product.authenticScans / product.totalScans) * 100}%`,
                      height: '100%',
                      backgroundColor: '#22c55e',
                      borderRadius: '3px'
                    }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Scans */}
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <h3 style={{
              color: '#1e293b',
              fontSize: '1.2rem',
              fontWeight: '600',
              margin: 0
            }}>
              Recent Scan Activity
            </h3>
            <button
              onClick={() => alert('Export functionality coming soon!')}
              style={{
                backgroundColor: '#f1f5f9',
                color: '#475569',
                padding: '8px 16px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Export Data
            </button>
          </div>

          <div style={{
            overflowX: 'auto'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr style={{
                  backgroundColor: '#f8fafc',
                  borderBottom: '2px solid #e2e8f0'
                }}>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    color: '#374151',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    Product
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    color: '#374151',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    Batch Code
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    color: '#374151',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    Result
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    color: '#374151',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    Location
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    color: '#374151',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    Time
                  </th>
                </tr>
              </thead>
              <tbody>
                {analytics.recentScans.map((scan) => (
                  <tr key={scan.id} style={{
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    <td style={{
                      padding: '12px 16px',
                      color: '#1e293b',
                      fontSize: '14px'
                    }}>
                      {scan.productName}
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      color: '#64748b',
                      fontSize: '14px',
                      fontFamily: 'monospace'
                    }}>
                      {scan.batchCode}
                    </td>
                    <td style={{
                      padding: '12px 16px'
                    }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: scan.result === 'authentic' ? '#f0fdf4' : 
                                       scan.result === 'duplicate' ? '#fef3c7' : '#fef2f2',
                        color: getResultColor(scan.result)
                      }}>
                        {getResultIcon(scan.result)} {scan.result.toUpperCase()}
                      </span>
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      color: '#64748b',
                      fontSize: '14px'
                    }}>
                      {scan.location}
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      color: '#64748b',
                      fontSize: '14px'
                    }}>
                      {scan.timestamp}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Alert Section */}
        {analytics.duplicateScans > 0 && (
          <div style={{
            backgroundColor: '#fef3c7',
            border: '2px solid #f59e0b',
            borderRadius: '16px',
            padding: '30px',
            marginTop: '30px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <span style={{ fontSize: '2rem' }}>⚠️</span>
              <h3 style={{
                color: '#92400e',
                fontSize: '1.3rem',
                fontWeight: '600',
                margin: 0
              }}>
                Counterfeit Activity Detected
              </h3>
            </div>
            
            <p style={{
              color: '#92400e',
              fontSize: '16px',
              marginBottom: '20px',
              lineHeight: '1.6'
            }}>
              We've detected {analytics.duplicateScans} duplicate scans, which may indicate counterfeit products 
              in the market. Consider investigating these locations and taking appropriate action.
            </p>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => alert('Report functionality coming soon!')}
                style={{
                  backgroundColor: '#92400e',
                  color: 'white',
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Generate Report
              </button>
              <a
                href="mailto:karuoyamercy@gmail.com?subject=Counterfeit Activity Alert"
                style={{
                  backgroundColor: 'white',
                  color: '#92400e',
                  padding: '10px 20px',
                  textDecoration: 'none',
                  border: '2px solid #92400e',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                Contact Support
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanAnalytics;
